import type { ProjectDetail } from "@esi/shared";
import { useConversationPage } from "../../conversation/queries.ts";
import { useWikiIndex } from "../../wiki/queries.ts";
import { useDocuments, useSummaryFollowsLists } from "../queries.ts";
import { useFigures } from "./Figures.tsx";
import { GettingStarted } from "./GettingStarted.tsx";
import { NeedsYou } from "./NeedsYou.tsx";
import { RECENT_CONVERSATIONS } from "./RecentConversations.tsx";
import { type HomeSectionEntry, visibleSections } from "./sections.ts";

/**
 * A project's Overview tab (spec `project-home`), its landing view: the sections the viewer's
 * capabilities allow, in two columns from 1024px (lists, then a 20rem column of figures) and one
 * column below. Conversations start from the header's ask field, on every tab. A project with
 * nothing the viewer can read yet shows Getting started instead of empty sections.
 */
export function ProjectOverview(props: {
  project: ProjectDetail;
  sections?: readonly HomeSectionEntry[];
}) {
  const { project } = props;
  useSummaryFollowsLists(project.id);
  const empty = useEmptyProject(project.id);
  const { main, side } = visibleSections(project, props.sections);
  return (
    <div data-esi-content="projects" className="mx-auto flex w-full max-w-6xl flex-col gap-7">
      {/* Until emptiness is known the sections show their own loading placeholders. */}
      {empty === true ? (
        // Work can wait on someone before any content exists (a first draft for review, an
        // access request, a task): Needs you stays above Getting started when it has rows.
        <div className="flex w-full flex-col gap-7 lg:max-w-3xl">
          <NeedsYou project={project} omitWhenEmpty />
          <GettingStarted project={project} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <Column sections={main} project={project} />
          <Column sections={side} project={project} />
        </div>
      )}
    </div>
  );
}

function Column(props: { sections: readonly HomeSectionEntry[]; project: ProjectDetail }) {
  if (props.sections.length === 0) {
    return null;
  }
  return (
    <div className="flex min-w-0 flex-col gap-7">
      {props.sections.map(({ id, Component }) => (
        <Component key={id} project={props.project} />
      ))}
    </div>
  );
}

/**
 * Whether the project has nothing the viewer can read yet: no wiki page, no source and no
 * conversation. Null until that is known (Getting started never flashes); a failed read counts as
 * content, so its section shows its own error and Retry. The knowledge counts come from the
 * summary, or from the wiki index and documents list while the summary route is missing.
 */
export function useEmptyProject(projectId: string): boolean | null {
  const { summary, fallback } = useFigures(projectId);
  const index = useWikiIndex(projectId, fallback);
  const documents = useDocuments(projectId, fallback);
  const knowledge: { pages: number; files: number } | null | "error" = fallback
    ? index.isError || documents.isError
      ? "error"
      : index.data && documents.data
        ? { pages: index.data.pages.length, files: documents.data.documents.length }
        : null
    : summary.isError
      ? "error"
      : summary.data
        ? { pages: summary.data.wiki.pages, files: summary.data.sources.files }
        : null;
  const noKnowledge =
    knowledge !== null && knowledge !== "error" && knowledge.pages === 0 && knowledge.files === 0;
  // Asked only once the knowledge is empty, as Recent conversations and the sidebar ask (one
  // shared request).
  const conversations = useConversationPage(
    { projectId, limit: RECENT_CONVERSATIONS },
    { enabled: noKnowledge },
  );
  if (knowledge === "error") {
    return false;
  }
  if (knowledge === null) {
    return null;
  }
  if (!noKnowledge) {
    return false;
  }
  if (conversations.isError) {
    return false;
  }
  return conversations.data === undefined ? null : conversations.data.items.length === 0;
}
